import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import cn from "mcn";
import { EditCurvesIcon } from "../icons/EditCurvesIcon";
import { EditPencilIcon } from "../icons/EditPencilIcon";
import { CreateFromCopyIcon } from "../icons/CreateFromCopyIcon";
import { CreateCurveIcon } from "../icons/CreateCurveIcon";
import { MenuItemIconWrapper } from "../icons/MenuItemIconWrapper";
import { SidebarIconWrapper } from "./SidebarIconWrapper";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { captureEvent } from "../../utils/o11y";
import { SelectCurveModal, type SelectCurveFor } from "../curve-management/SelectCurveModal";
import { useUserSubscriptionTier } from "../../data/user";

const menuItemClass = cn(
  "flex w-full items-center gap-[length:var(--spacing-scale-small-200,6px)] px-3 py-2",
  "text-left text-[13px] leading-[20px] font-normal whitespace-nowrap",
  "text-[var(--foreground-default,#101010)] cursor-pointer",
  "hover:bg-neutral-tonal rounded-sm",
  "disabled:opacity-40 disabled:cursor-not-allowed",
);

export function CurveManagementNavButton() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectCurveFor, setSelectCurveFor] = useState<SelectCurveFor | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { disabledFeatures } = useUserSubscriptionTier();

  const isCreateDisabled = Boolean(disabledFeatures?.createCurve);
  const isEditDisabled = Boolean(disabledFeatures?.editCurve);
  const isAllDisabled = isCreateDisabled && isEditDisabled;

  const tooltipTitle = isAllDisabled ? "Please upgrade your subscription to access this feature" : "Curve Management";

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuPosition(null);
  }, []);

  // Match Actions menu: clicking the trigger again closes the menu.
  // Outside-click must ignore the trigger so it doesn't fight the toggle.
  const handleTriggerClick = () => {
    if (isAllDisabled) return;
    if (menuOpen) {
      closeMenu();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPosition({ top: rect.top, left: rect.right + 8 });
    setMenuOpen(true);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [menuOpen, closeMenu]);

  const handleCreate = () => {
    closeMenu();
    if (isCreateDisabled) return;
    captureEvent("clicked_create_curves");
    navigate({ to: "/app/curve-management", search: { mode: "create" } });
  };

  const handleCopy = () => {
    closeMenu();
    if (isCreateDisabled) return;
    setSelectCurveFor("copy");
  };

  const handleEdit = () => {
    closeMenu();
    if (isEditDisabled) return;
    captureEvent("clicked_edit_curves");
    setSelectCurveFor("edit");
  };

  const handleCurveSelected = (curveId: string) => {
    if (selectCurveFor === "copy") {
      captureEvent("clicked_copy_curve_from_nav");
      navigate({ to: "/app/curve-management", search: { mode: "create", copy: curveId } });
    } else if (selectCurveFor === "edit") {
      captureEvent("clicked_edit_curves");
      navigate({ to: "/app/curve-management", search: { mode: "edit", edit: curveId } });
    }
    setSelectCurveFor(null);
  };

  return (
    <>
      <TooltipTrigger placement="right">
        <SidebarIconWrapper ref={triggerRef}>
          <button
            type="button"
            className={isAllDisabled ? "cursor-not-allowed" : ""}
            onClick={handleTriggerClick}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <EditCurvesIcon disabled={isAllDisabled} />
          </button>
        </SidebarIconWrapper>
        <Tooltip>{tooltipTitle}</Tooltip>
      </TooltipTrigger>

      {menuOpen &&
        menuPosition &&
        createPortal(
          <CurveNavDropdown
            position={menuPosition}
            triggerRef={triggerRef}
            onClose={closeMenu}
            isCreateDisabled={isCreateDisabled}
            isEditDisabled={isEditDisabled}
            onCreate={handleCreate}
            onCopy={handleCopy}
            onEdit={handleEdit}
          />,
          document.body,
        )}

      {selectCurveFor &&
        createPortal(
          <SelectCurveModal
            mode={selectCurveFor}
            onConfirm={handleCurveSelected}
            onCancel={() => setSelectCurveFor(null)}
          />,
          document.body,
        )}
    </>
  );
}

function CurveNavDropdown({
  position,
  triggerRef,
  onClose,
  isCreateDisabled,
  isEditDisabled,
  onCreate,
  onCopy,
  onEdit,
}: {
  position: { top: number; left: number };
  triggerRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  isCreateDisabled: boolean;
  isEditDisabled: boolean;
  onCreate: () => void;
  onCopy: () => void;
  onEdit: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside (ignore the trigger — it handles toggle itself)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [onClose, triggerRef]);

  return (
    <div
      ref={menuRef}
      role="menu"
      className="fixed z-[9999] flex w-max flex-col p-[length:var(--spacing-scale-small-300,9px)] bg-[var(--color-card,#fff)] border border-[var(--color-outline)] rounded-md shadow-lg"
      style={{ top: position.top, left: position.left }}
    >
      <button type="button" role="menuitem" className={menuItemClass} disabled={isCreateDisabled} onClick={onCreate}>
        <MenuItemIconWrapper>
          <CreateCurveIcon />
        </MenuItemIconWrapper>
        Create new Curve
      </button>

      <button type="button" role="menuitem" className={menuItemClass} disabled={isCreateDisabled} onClick={onCopy}>
        <MenuItemIconWrapper>
          <CreateFromCopyIcon />
        </MenuItemIconWrapper>
        Create Curve from copy
      </button>

      <button type="button" role="menuitem" className={menuItemClass} disabled={isEditDisabled} onClick={onEdit}>
        <MenuItemIconWrapper>
          <EditPencilIcon />
        </MenuItemIconWrapper>
        Edit an existing Curve
      </button>
    </div>
  );
}
